import useSubmitOnblur from '@/utils/useSubmitOnBlur';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

interface IArgs<K extends string> {
	isLoading: boolean;
	defaultValue: Record<K, string> | undefined;
	onChange: (v: Record<string, string>) => void;
}

export function useTagInput<K extends string>(args: IArgs<K>) {
	const { t } = useTranslation();
	// Form only used for validation.
	const form = useForm<FieldValues>({ defaultValues: args.defaultValue });
	const tagForm = useForm({
		mode: 'all',
		defaultValues: { tag: '' },
		resolver: yupResolver(
			object().shape({
				tag: string().required(t('Required')),
			})
		),
	});

	function submitForm(values: Record<string, string>) {
		return args.onChange(values);
	}

	const { registerSubmitOnBlur } = useSubmitOnblur(form, (e) => {
		const values: Record<string, string> = args.defaultValue || {};
		values[e.target.name] = e.target.value;
		submitForm(values);
	});

	function deleteTag(key: keyof FieldValues) {
		const values: Record<string, string> = args.defaultValue || {};
		delete values[key];
		submitForm(values);
	}

	function addTag(key: string) {
		const values: Record<string, string> = args.defaultValue || {};
		values[key] = '';
		submitForm(values);
	}

	const handleTagFormSubmit = tagForm.handleSubmit((values) => {
		addTag(values.tag);
		tagForm.reset();
	});

	return {
		isLoading: args.isLoading || form.formState.isSubmitting || tagForm.formState.isSubmitting,
		t,
		tagForm,
		form,
		registerSubmitOnBlur,
		deleteTag,
		addTag,
		handleTagFormSubmit,
	};
}
